import React, { useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { isEmpty, omit } from 'lodash';
import * as Yup from 'yup';

import useClaimFlagConfigurations from '~/components/ClaimFlags/hooks/useClaimFlagConfigurations';
import type { ClaimFlagConfiguration } from '~/components/ClaimFlags/types';
import useClaimStageConfigurations from '~/components/ClaimStages/hooks/useClaimStageConfigurations';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import {
  useFormikValidationErrorToast,
  ValidationErrorToastContainer,
} from '~/components/hooks/useFormikValidationErrorToast';
import { getLocalDateToday } from '~/DateTimeUtils';
import { FNOL_MIXPANEL_EVENTS } from '~/pocs/mixpanel';

import mixpanel from '../../../CmsMain/mixpanel';
import { useIncidentConfiguration } from '../../../hooks/useIncidentConfiguration';
import { PreDefinedField, preparePreDefinedFields } from '../../../IncidentConfiguration/ConfiguredFields';
import { getAdditionalDataValidations } from '../../../IncidentConfiguration/CustomFieldsContext';
import { isFutureDateOfLossAllowed } from '../../../IncidentConfiguration/utils';
import LoadingDialog from '../../../LoadingDialog';
import { usePolicy } from '../../../PolicyContainer';
import type { IncidentConfigurationBase } from '../../../types/incident-configuration-types';
import { getPredefinedIncidentDetailsFieldsInitialValues } from '../IncidentDetails';

interface ClaimValues {
  reported_date?: string;
  date_of_loss?: string;
  submission_mode?: string;
  stage_key?: string;
}

interface PartiesStore {
  isFetching: boolean;
}

interface FnolFormikValues {
  reported_date?: string;
  date_of_loss?: string;
  submission_mode?: string;
  stage_key?: string;

  [key: string]: unknown;
}

interface FnolFooterProps {
  onSaveDraft: (values: FnolFormikValues, setSubmitting: (isSubmitting: boolean) => void) => Promise<void>;
  partiesStore: PartiesStore;
  hideSaveDraft?: boolean;
  mixpanelExtra?: Record<string, unknown> | null;
  /** Enabled flags in the claim's sub-org/LoB scope; the claim_flags field is offered only when non-empty. */
  inScopeEnabledFlags?: ClaimFlagConfiguration[];
}

const getFooterInitialValues = (
  incidentConfiguration: IncidentConfigurationBase,
  claimValues?: ClaimValues,
  initialStageKey?: string | null
) => ({
  ...getPredefinedIncidentDetailsFieldsInitialValues(omit(incidentConfiguration.fnol, 'instructions')),
  reported_date: claimValues?.reported_date || getLocalDateToday(),
  ...(initialStageKey && !incidentConfiguration.fnol?.stage_key?.mandatory ? { stage_key: initialStageKey } : {}),
  // The claim_flags field toggle lives under fnol.claim_flags; its values are collected in claim_flag_keys.
  // Seeded empty here; a resumed draft's keys arrive via the later draft spread and are pruned to the
  // in-scope set by FnolFooter once configs load (see the useEffect there).
  ...(incidentConfiguration.fnol?.claim_flags?.active ? { claim_flag_keys: [] } : {}),
});

const getFooterValidation = (
  incidentConfiguration: IncidentConfigurationBase,
  enabledStageKeys?: string[],
  enabledInScopeFlagKeys?: string[]
) => {
  const allowFuture = isFutureDateOfLossAllowed(incidentConfiguration);
  let reportedDateThenSchema = Yup.date().nullable().required('Required');
  if (!allowFuture) {
    reportedDateThenSchema = reportedDateThenSchema.min(Yup.ref('date_of_loss'), 'Must be after the Date of Loss');
  }

  // claim_flags submits under claim_flag_keys, so omit it from the generic predefined-field validations.
  const fnolFields = omit(incidentConfiguration.fnol, 'instructions', 'stage_key', 'claim_flags');
  const stageKeyField = incidentConfiguration.fnol?.stage_key;
  const stageKeyValidation =
    stageKeyField?.active && stageKeyField?.mandatory && enabledStageKeys?.length
      ? { stage_key: Yup.string().oneOf(enabledStageKeys).required('Required') }
      : {};

  const claimFlagsField = incidentConfiguration.fnol?.claim_flags;
  let claimFlagKeysValidation = {};
  if (claimFlagsField?.active && enabledInScopeFlagKeys?.length) {
    let flagKeysSchema = Yup.array().of(Yup.string().oneOf(enabledInScopeFlagKeys));
    if (claimFlagsField.mandatory) {
      flagKeysSchema = flagKeysSchema.min(1, 'Required').required('Required');
    }
    claimFlagKeysValidation = { claim_flag_keys: flagKeysSchema };
  }

  return {
    ...getAdditionalDataValidations(preparePreDefinedFields(fnolFields)),
    reported_date: Yup.date()
      .max(new Date())
      .nullable()
      .required('Required')
      .when('date_of_loss', {
        is: (val: unknown) => !!val,
        then: reportedDateThenSchema,
      }),
    ...stageKeyValidation,
    ...claimFlagKeysValidation,
  };
};

// Prune claim_flag_keys down to the currently offered in-scope keys once the flag configs have loaded.
// Reactive (not at Formik init) so it never fires while configs are still loading — otherwise a resumed
// draft's keys would be dropped on a cold load. A draft key that was since disabled/re-scoped is absent from
// the picker options, so pruning it here avoids a Yup .oneOf failure with no way to deselect it.
const usePruneStaleClaimFlagKeys = (inScopeEnabledFlags: ClaimFlagConfiguration[]) => {
  const { values, setFieldValue } = useFormikContext<FnolFormikValues>();
  const { isLoading: flagsLoading, isError: flagsError } = useClaimFlagConfigurations();

  // The signature is a stable dep (the array identity changes every render); the memo rebuilds the allowed
  // key list only when the set of in-scope flag keys actually changes, so the effect's deps stay honest.
  const allowedKeysSignature = inScopeEnabledFlags.map((flag) => flag.key).join(',');
  const allowedKeys = useMemo(() => allowedKeysSignature.split(',').filter(Boolean), [allowedKeysSignature]);
  const selectedKeys = values.claim_flag_keys;

  useEffect(() => {
    if (flagsLoading || flagsError) {
      return;
    }
    if (!Array.isArray(selectedKeys) || selectedKeys.length === 0) {
      return;
    }
    const allowed = new Set(allowedKeys);
    const pruned = selectedKeys.filter((key) => allowed.has(key));
    if (pruned.length !== selectedKeys.length) {
      setFieldValue('claim_flag_keys', pruned);
    }
  }, [flagsLoading, flagsError, allowedKeys, selectedKeys, setFieldValue]);
};

const FnolFooter: React.FC<FnolFooterProps> = ({
  onSaveDraft,
  partiesStore,
  hideSaveDraft = false,
  mixpanelExtra,
  inScopeEnabledFlags = [],
}) => {
  const { handleSubmit, isSubmitting, setSubmitting, values } = useFormikContext<FnolFormikValues>();
  const { incidentConfiguration } = useIncidentConfiguration();
  // Omit claim_flags: it submits under claim_flag_keys (injected below), so the raw config entry is a
  // dead multiselect with no Formik field — keep it out of the generic PreDefinedField map.
  const preDefinedFields = omit(incidentConfiguration.fnol, 'instructions', 'claim_flags');
  const { policy } = usePolicy();
  const { claimStageConfigurations, isOperational: isClaimStageOperational } = useClaimStageConfigurations();
  const enabledStages = claimStageConfigurations.filter((config) => config.is_enabled);
  const toastId = 'fnol-form-errors';

  const stageKeyField = preDefinedFields.stage_key;
  const isStageKeyVisible = !!stageKeyField?.active && isClaimStageOperational && !isEmpty(enabledStages);

  if (isStageKeyVisible && stageKeyField) {
    preDefinedFields.stage_key = {
      ...stageKeyField,
      options: enabledStages.map((s) => ({ id: s.key, desc: s.label })),
    };
  }

  // The claim_flags field toggle lives under fnol.claim_flags but its values are collected under
  // claim_flag_keys; the field is offered only when active and at least one in-scope enabled flag exists.
  const claimFlagsField = incidentConfiguration.fnol?.claim_flags;
  const isClaimFlagsVisible = !!claimFlagsField?.active && !isEmpty(inScopeEnabledFlags);

  if (isClaimFlagsVisible && claimFlagsField) {
    const flagOrder = inScopeEnabledFlags.map((flag) => flag.key);
    preDefinedFields.claim_flag_keys = {
      ...claimFlagsField,
      type: 'multiselect',
      options: inScopeEnabledFlags.map((flag) => ({ id: flag.key, desc: flag.label })),
      // Render the selected summary in configured flag order (not selection order) to match the picker.
      sortSelected: (a: string, b: string) => flagOrder.indexOf(a) - flagOrder.indexOf(b),
    };
  }

  usePruneStaleClaimFlagKeys(inScopeEnabledFlags);

  useFormikValidationErrorToast({ toastId });

  const handleSubmitDraft = async () => {
    await onSaveDraft(values, setSubmitting);
  };

  const handleOnClickCreated = () => {
    handleSubmit();

    // Base event metadata
    const baseMetadata = {
      date_of_report: values.reported_date,
      submission_mode: values.submission_mode,
      reporting_type: policy.is_manual ? 'reported without policy' : 'reported from existing policy',
      fnol_type: 'new_fnol',
    };

    // Add AI metadata if available (when FNOL originated from AI inbox)
    const eventMetadata = mixpanelExtra
      ? {
          ...baseMetadata,
          ...mixpanelExtra,
        }
      : baseMetadata;

    mixpanel.track(FNOL_MIXPANEL_EVENTS.CREATE_FNOL_CLICKED, eventMetadata);
  };
  return (
    <>
      <ValidationErrorToastContainer containerId={toastId} />
      <div className="fixed bottom-0 z-50 h-80 w-full bg-white shadow-xl">
        <Grid container className="mx-20 my-0 flex w-full items-stretch justify-center" spacing={2}>
          <PreDefinedField
            id="submission_mode"
            fields={preDefinedFields}
            inline
            gridXs={2 as never}
            value={undefined}
            onUpdate={undefined}
          />
          <PreDefinedField
            id="reported_date"
            fields={preDefinedFields}
            inline
            gridXs={2 as never}
            disableFuture
            value={undefined}
            onUpdate={undefined}
          />
          {isStageKeyVisible ? (
            <PreDefinedField
              id="stage_key"
              fields={preDefinedFields}
              inline
              gridXs={2 as never}
              value={undefined}
              onUpdate={undefined}
            />
          ) : null}
          {isClaimFlagsVisible ? (
            <PreDefinedField
              id="claim_flag_keys"
              fields={preDefinedFields}
              inline
              gridXs={2 as never}
              value={undefined}
              onUpdate={undefined}
            />
          ) : null}

          <Grid item className="mt-16 flex h-full items-center justify-center gap-12">
            {!hideSaveDraft && (
              <Button
                disabled={isSubmitting || partiesStore.isFetching}
                onClick={async () => {
                  setSubmitting(true);
                  await handleSubmitDraft();
                }}
                variant="outlined"
                className="border border-blue-600 text-blue-600"
              >
                Save Draft
              </Button>
            )}
            <Button disabled={isSubmitting} onClick={handleOnClickCreated} variant="contained" color="primary">
              Create Claim
            </Button>
          </Grid>
        </Grid>
      </div>
      {isSubmitting && <LoadingDialog isError={false} track="Submit configurable fnol" />}
    </>
  );
};

export { getFooterInitialValues, getFooterValidation };

export default FnolFooter;
